import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Box
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setError('Verification token is missing. Please check your email for the correct verification link.');
      setLoading(false);
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-email?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setUserEmail(data.user?.email || '');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.message || 'Email verification failed. The link may have expired or is invalid.');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0927eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          style={{
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <div style={{ marginBottom: '32px' }}>
            <Logo size="large" />
          </div>

          {loading && (
            <Box>
              <CircularProgress size={60} style={{ marginBottom: '24px', color: '#0927eb' }} />
              <Typography variant="h5" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#0927eb' }}>
                Verifying Your Email...
              </Typography>
              <Typography variant="body1" style={{ color: '#666' }}>
                Please wait while we verify your email address.
              </Typography>
            </Box>
          )}

          {success && !loading && (
            <Box>
              <CheckCircleIcon 
                style={{ 
                  fontSize: 80, 
                  color: '#4caf50', 
                  marginBottom: '24px' 
                }} 
              />
              <Typography variant="h4" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#0927eb' }}>
                Email Verified Successfully!
              </Typography>
              <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
                Your email address {userEmail && `(${userEmail})`} has been successfully verified.
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '32px', color: '#666' }}>
                You can now log in to your account. Redirecting to login page...
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                style={{
                  backgroundColor: '#000000',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '12px 32px'
                }}
              >
                Go to Login
              </Button>
            </Box>
          )}

          {error && !loading && (
            <Box>
              <ErrorIcon 
                style={{ 
                  fontSize: 80, 
                  color: '#f44336', 
                  marginBottom: '24px' 
                }} 
              />
              <Typography variant="h5" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#0927eb' }}>
                Verification Failed
              </Typography>
              <Alert severity="error" style={{ marginBottom: '24px', textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant="body2" style={{ marginBottom: '24px', color: '#666' }}>
                If you need a new verification email, please contact support or try registering again.
              </Typography>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  style={{
                    borderColor: '#0927eb',
                    color: '#0927eb',
                    fontWeight: 'bold'
                  }}
                >
                  Go to Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  style={{
                    backgroundColor: '#000000',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  Register Again
                </Button>
              </div>
            </Box>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default VerifyEmail;

